import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Divider,
  FormControl,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TextFieldProps,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useAppSelector } from '../../../../hooks';
import { TPTextField } from '../../../../components/Inputs';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../components/V2/Table/type1/TableHeadCustom';
import SubmissionTableRow from './components/SubmissionTableRow';
import { getSubmissionsV2State } from '../../../../store/V2/SubmissionsSlice';
import { getCourseStudentsState } from '../../../../store/V2/CourseStudentsSlice';
import StudentDetailedOverview from '../StudentDetailedOverview';

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedStudent?: TSelectOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '54px',
      fontSize: '14px',
      borderRadius: '10px',
      width: { xs: '100%', sm: '500px' },
    },
  },
};

const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultState: TState = {
  selectedStudent: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'studentName',
    label: 'Student Name',
    sortable: false,
  },
  {
    id: 'lastSubmission',
    label: 'Last Submission',
    sortable: false,
    minWidth: 120,
  },
  {
    id: 'score',
    label: 'Score (Avg)',
    sortable: false,
  },
  {
    id: 'timeTaken',
    label: 'Time taken (Avg)',
    sortable: false,
    minWidth: 80,
  },
  {
    id: 'action',
    label: 'Action',
    sortable: false,
  },
];

interface IStudentDetailedOverviewProps {
  selectedStudent: TSelectOption | null;
}

const DetailedOverview: React.FC<IStudentDetailedOverviewProps> = ({
  selectedStudent,
}) => {
  // Store
  const { groupedStudentsSubmissions, status } = useAppSelector(
    getSubmissionsV2State
  );
  const { courseStudents } = useAppSelector(getCourseStudentsState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState(defaultState);
  const [studentOptions, setStudentOptions] = useState<TSelectOption[]>([]);

  const filteredGroupedStudentsSubmissions =
    groupedStudentsSubmissions && groupedStudentsSubmissions.length
      ? groupedStudentsSubmissions.filter((item) => {
          if (selectedStudent) {
            return selectedStudent.value === item._id;
          }

          return true;
        })
      : [];

  useEffect(() => {
    updateStateAttributes({ selectedStudent });
  }, [selectedStudent]);

  useEffect(() => {
    const options = courseStudents.map((courseStudent) => ({
      label: `${courseStudent.student.firstname} ${
        courseStudent.student.lastname || ''
      }`,
      value: courseStudent.student._id,
    }));

    setStudentOptions(options);
  }, [courseStudents]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleStudentChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedStudent: value,
    }));
  };

  if (filteredGroupedStudentsSubmissions.length && status === 'idle') {
    return (
      <div>
        <Divider flexItem sx={{ borderStyle: 'dashed', margin: '32px 0px' }} />

        <Typography fontSize="30px" fontWeight="600">
          Detailed overview
        </Typography>

        <Stack
          boxShadow="0px 14px 40px 0px #6E9DB22E"
          borderRadius="20px"
          paddingBottom="68px"
          marginTop="32px"
        >
          {!selectedStudent && (
            <FormControl sx={{ margin: '20px' }}>
              <Autocomplete
                size="small"
                value={state.selectedStudent}
                options={studentOptions}
                onChange={handleStudentChange}
                popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    placeholder="Select Student"
                  />
                )}
              />
            </FormControl>
          )}

          {state.selectedStudent ? (
            <StudentDetailedOverview selectedStudent={state.selectedStudent} />
          ) : (
            <TableContainer
              sx={{
                position: 'relative',
              }}
            >
              <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={tableData.order}
                  orderBy={tableData.orderBy}
                  headLabels={tableHeaderLabels}
                  rowCount={0}
                  numSelected={tableData.selected.length}
                />

                <TableBody>
                  {filteredGroupedStudentsSubmissions.map((row) => (
                    <SubmissionTableRow key={row._id} rowData={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </div>
    );
  }

  return null;
};

export default DetailedOverview;
